/**
 * OCR Service using Tesseract.js
 * Extracts text from images/documents
 */

import { createWorker } from 'tesseract.js'

let worker = null
let currentProgressCallback = null

/**
 * Performs OCR on an image
 * @param {string|HTMLCanvasElement|Blob|File} image - Image to process
 * @param {Object} options - OCR options
 * @param {string} options.language - Language(s) to use (default: 'eng')
 * @param {function} options.onProgress - Progress callback (0-100)
 * @returns {Promise<Object>} OCR result with text and confidence
 */
export async function performOCR(image, options = {}) {
  const { language = 'eng', onProgress } = options

  try {
    // Store progress callback for logger
    currentProgressCallback = onProgress

    // Terminate existing worker if language changed
    if (worker) {
      await worker.terminate()
      worker = null
    }

    // Create new worker with progress logger
    console.log('[OCR] Creating worker for language:', language)
    worker = await createWorker(language, 1, {
      logger: (m) => {
        console.log('[OCR] Status:', m.status, m.progress)
        if (currentProgressCallback && m.status === 'recognizing text') {
          currentProgressCallback(Math.round(m.progress * 100))
        }
      }
    })

    console.log('[OCR] Worker created, starting recognition...')
    
    // Perform recognition (simpler API without progress in recognize call)
    const result = await worker.recognize(image)

    console.log('[OCR] Recognition complete, confidence:', result.data.confidence)

    return {
      text: result.data.text,
      confidence: result.data.confidence,
      words: result.data.words,
      lines: result.data.lines,
      paragraphs: result.data.paragraphs,
      blocks: result.data.blocks
    }
  } catch (error) {
    console.error('[OCR] Failed:', error)
    throw error
  } finally {
    currentProgressCallback = null
  }
}

/**
 * Initializes OCR worker (optional pre-loading)
 */
export async function initializeOCR(language = 'eng') {
  if (!worker) {
    worker = await createWorker(language, 1, {
      logger: (m) => {
        if (currentProgressCallback && m.status === 'recognizing text') {
          currentProgressCallback(Math.round(m.progress * 100))
        }
      }
    })
  }
  return worker
}

/**
 * Terminates the OCR worker to free resources
 */
export async function terminateOCR() {
  if (worker) {
    try {
      await worker.terminate()
    } catch (e) {
      console.warn('[OCR] Error terminating worker:', e)
    }
    worker = null
  }
  currentProgressCallback = null
}

/**
 * Available languages for OCR
 * Multi-language combinations use + notation (e.g., 'eng+hin')
 */
export const OCR_LANGUAGES = [
  // Common multi-language combinations (recommended for mixed documents)
  { code: 'eng+hin', name: '🇮🇳 English + Hindi (Recommended for Indian docs)', recommended: true },
  { code: 'eng+hin+mar', name: '🇮🇳 English + Hindi + Marathi' },
  { code: 'eng+hin+tam', name: '🇮🇳 English + Hindi + Tamil' },
  { code: 'eng+hin+tel', name: '🇮🇳 English + Hindi + Telugu' },
  { code: 'eng+hin+kan', name: '🇮🇳 English + Hindi + Kannada' },
  { code: 'eng+hin+ben', name: '🇮🇳 English + Hindi + Bengali' },
  
  // Divider
  { code: '', name: '── Single Languages ──', disabled: true },
  
  // Single languages
  { code: 'eng', name: 'English only' },
  { code: 'hin', name: 'Hindi only (हिन्दी)' },
  { code: 'tam', name: 'Tamil (தமிழ்)' },
  { code: 'tel', name: 'Telugu (తెలుగు)' },
  { code: 'kan', name: 'Kannada (ಕನ್ನಡ)' },
  { code: 'mal', name: 'Malayalam (മലയാളം)' },
  { code: 'mar', name: 'Marathi (मराठी)' },
  { code: 'ben', name: 'Bengali (বাংলা)' },
  { code: 'guj', name: 'Gujarati (ગુજરાતી)' },
  { code: 'pan', name: 'Punjabi (ਪੰਜਾਬੀ)' },
  { code: 'ori', name: 'Odia (ଓଡ଼ିଆ)' },
  { code: 'urd', name: 'Urdu (اردو)' },
  
  // International
  { code: '', name: '── International ──', disabled: true },
  { code: 'spa', name: 'Spanish' },
  { code: 'fra', name: 'French' },
  { code: 'deu', name: 'German' },
  { code: 'ara', name: 'Arabic' },
  { code: 'chi_sim', name: 'Chinese (Simplified)' },
  { code: 'jpn', name: 'Japanese' }
]

/**
 * Copies text to clipboard
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    // Fallback for older browsers
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const success = document.execCommand('copy')
    document.body.removeChild(textarea)
    return success
  }
}
